import { PUBLIC_DIFFUSION_API_ENDPOINT } from '$env/static/public';
import type { ProjectCreationResult } from '$lib/aqueduct/projectDuplication';

export type GraphProjectSummary = {
	graph_project_id: string;
	workspace_id: string;
	workspace_display_name: string;
	created_by_user_id: string;
	capabilities: string[];
	display_name: string;
	version: number;
	created_at: string;
	last_updated_at: string;
	/** 256px thumbnail of the project's latest snapshot, if any. */
	thumbnail_url: string | null;
};

export type GraphProjectDetail = {
	graph_project_id: string;
	workspace_id: string;
	created_by_user_id: string;
	capabilities: string[];
	display_name: string;
	content: Record<string, unknown>;
	version: number;
	created_at: string;
	last_updated_at: string;
};

export class ProjectDisplayNameConflictError extends Error {
	constructor(
		public readonly displayName: string,
		public readonly suggestion: string
	) {
		super(`A project named "${displayName}" already exists`);
		this.name = 'ProjectDisplayNameConflictError';
	}
}

export class GraphProjectsClient {
	private token: string;
	private baseUrl: string;

	constructor(token: string) {
		this.token = token;
		this.baseUrl = PUBLIC_DIFFUSION_API_ENDPOINT;
	}

	public setToken(token: string) {
		this.token = token;
	}

	public async getProjects(workspaceId?: string): Promise<GraphProjectSummary[]> {
		const query = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
		const response = await fetch(`${this.baseUrl}/graph-projects${query}`, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${this.token}`
			}
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch projects: ${response.status}`);
		}

		const data = await response.json();
		return data.projects;
	}

	public async createProject(
		name: string,
		workspaceId?: string,
		content?: Record<string, unknown>
	): Promise<ProjectCreationResult> {
		const response = await fetch(`${this.baseUrl}/graph-project`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${this.token}`,
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				display_name: name || undefined,
				workspace_id: workspaceId,
				content
			})
		});

		if (!response.ok) {
			throw new Error(`Failed to create project: ${response.status}`);
		}

		return (await response.json()) as ProjectCreationResult;
	}

	public async getProject(projectId: string): Promise<GraphProjectDetail> {
		const response = await fetch(`${this.baseUrl}/graph-project/${projectId}`, {
			headers: { Authorization: `Bearer ${this.token}` }
		});
		if (!response.ok) throw new Error(`Failed to fetch project: ${response.status}`);
		return await response.json();
	}

	public async renameProject(projectId: string, displayName: string): Promise<string> {
		const response = await fetch(`${this.baseUrl}/graph-project/${projectId}`, {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${this.token}`,
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ display_name: displayName })
		});
		if (response.status === 409) {
			const payload = await response.json();
			const detail = payload.detail ?? {};
			throw new ProjectDisplayNameConflictError(
				detail.display_name ?? displayName,
				detail.suggestion ?? displayName
			);
		}
		if (!response.ok) throw new Error(`Failed to rename project: ${response.status}`);
		return (await response.json()).display_name;
	}

	public async trashProject(projectId: string): Promise<void> {
		const response = await fetch(`${this.baseUrl}/graph-project/${projectId}`, {
			method: 'DELETE',
			headers: {
				Authorization: `Bearer ${this.token}`
			}
		});

		if (!response.ok) {
			throw new Error(`Failed to move project to Trash: ${response.status}`);
		}
	}

	public async duplicateProject(
		projectId: string,
		workspaceId?: string
	): Promise<ProjectCreationResult> {
		const response = await fetch(`${this.baseUrl}/graph-project/${projectId}/duplicate`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${this.token}`,
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ workspace_id: workspaceId })
		});

		if (!response.ok) {
			throw new Error(`Failed to duplicate project: ${response.status}`);
		}

		return (await response.json()) as ProjectCreationResult;
	}

	public async getTrashedProjects(workspaceId: string): Promise<GraphProjectSummary[]> {
		const response = await fetch(
			`${this.baseUrl}/trash/projects?workspace_id=${encodeURIComponent(workspaceId)}`,
			{ headers: { Authorization: `Bearer ${this.token}` } }
		);
		if (!response.ok) throw new Error(`Failed to fetch Trash: ${response.status}`);
		return (await response.json()).projects;
	}

	public async restoreProject(projectId: string, displayName?: string): Promise<string> {
		const response = await fetch(`${this.baseUrl}/graph-project/${projectId}/restore`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${this.token}`,
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ display_name: displayName })
		});
		if (response.status === 409) {
			const payload = await response.json();
			const detail = payload.detail ?? {};
			throw new ProjectDisplayNameConflictError(
				detail.display_name ?? displayName ?? '',
				detail.suggestion ?? displayName ?? ''
			);
		}
		if (!response.ok) throw new Error(`Failed to restore project: ${response.status}`);
		return (await response.json()).display_name;
	}
}
